import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Download, Image as ImageIcon, Film, CheckCircle, 
  Loader2, AlertCircle, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../App';

export default function SharedGallery() {
  const { token } = useParams();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadedIds, setDownloadedIds] = useState(new Set());

  useEffect(() => {
    const fetchSharedFiles = async () => {
      try {
        const response = await axios.get(`${API}/media/share/${token}`);
        setFiles(response.data.files || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Ссылка недействительна или истекла');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedFiles();
    }
  }, [token]);

  // Download single file
  const handleDownload = async (file) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Mark as downloaded
      setDownloadedIds(prev => new Set([...prev, file.id]));
      
      // Track download
      axios.post(`${API}/media/share/${token}/download`).catch(() => {});
    } catch (error) {
      window.open(file.url, '_blank');
    }
  };

  // Download all files
  const downloadAll = async () => {
    setDownloadingAll(true);
    
    for (const file of files) {
      await handleDownload(file);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setDownloadingAll(false);
    toast.success('Все файлы скачаны!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ссылка недействительна</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/">
            <Button>На главную</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Общий доступ</h1>
                <p className="text-sm text-gray-500">{files.length} файл(ов)</p>
              </div>
            </div>
            {files.length > 1 && (
              <Button 
                onClick={downloadAll}
                disabled={downloadingAll}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingAll ? 'Скачивание...' : 'Скачать все'}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Files Grid */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {files.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-gray-900">Файлы не найдены</h2>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {files.map((file) => (
              <div
                key={file.id}
                className="bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Preview */}
                <div className="aspect-square bg-gray-100 relative">
                  {file.file_type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <Film className="w-16 h-16 text-gray-400" />
                    </div>
                  ) : (
                    <img 
                      src={file.url} 
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                  
                  {/* Downloaded indicator */}
                  {downloadedIds.has(file.id) && (
                    <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                  )}
                </div>

                {/* Info & Actions */}
                <div className="p-4">
                  <h3 className="font-medium text-gray-900 truncate mb-3">
                    {file.name || file.filename}
                  </h3>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleDownload(file)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Скачать
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(file.url, '_blank')}
                      title="Открыть в новой вкладке"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-5xl mx-auto px-4 py-6 text-center text-sm text-gray-500">
          <p>Ссылка создана для обмена файлами</p>
        </div>
      </footer>
    </div>
  );
}
